/**
 * Dynamic Icon Component
 * 
 * Renders Lucide icons dynamically by name string.
 * Used for displaying category icons from the backend API.
 */

import {
  Utensils, Sandwich, Coffee, Beer, Croissant, ShoppingCart, Store, UtensilsCrossed,
  IceCream2, Landmark, CreditCard, Banknote, Send, Cross, Stethoscope, UserRound,
  Smile, Pill, PawPrint, HeartPulse, School, Baby, BookOpen, GraduationCap, Library,
  Bus, Car, ParkingSquare, Fuel, Wrench, Hotel, Bed, BedDouble, BedSingle,
  Home, ShoppingBag, Shirt, Smartphone, Sofa, Building2, Scale, Shield, Flame, Mail,
  Church, MapPin, Navigation, Navigation2, Building,
  Trees, FerrisWheel, Dumbbell, Waves, Trophy, Umbrella, Star, Eye, Palette,
  Clapperboard, Drama, Camera, Apple, Palmtree, TreePine, TreeDeciduous,
  Sprout, Leaf, Shrub, Sun, CloudRain, Mountain, Triangle, Circle, CircleDot,
  Flower2, Droplets, Droplet, LandPlot, MountainSnow, Layers,
  Anchor, Axe, Beef, Bike, Blocks, BrickWall, Briefcase, Castle, Construction,
  Crown, Cylinder, Database, DoorOpen, Dribbble, Factory, Fence, Footprints,
  Grid2x2, HardHat, Lock, LogIn, Map, Milestone, Minus,   MoveHorizontal, Package,
  Pipette, Plane, PlaneLanding, PlaneTakeoff, Recycle, RotateCcw, Route, Scissors,
  Ship, Signpost, Square, Tractor, TrafficCone, Train, Trash2, TrendingUp, Users,
  Warehouse, Wheat, Wind, FileText, PersonStanding, ArrowUpDown, Lamp,
  type LucideProps
} from 'lucide-react';
import { ComponentType } from 'react';

// Map of icon names to Lucide components
const iconComponents: Record<string, ComponentType<LucideProps>> = {
  // Food & Drink
  'utensils': Utensils,
  'hamburger': Sandwich,
  'coffee': Coffee,
  'beer': Beer,
  'croissant': Croissant,
  'shopping-cart': ShoppingCart,
  'store': Store,
  'utensils-crossed': UtensilsCrossed,
  'ice-cream-cone': IceCream2,
  // Financial
  'landmark': Landmark,
  'credit-card': CreditCard,
  'banknote': Banknote,
  'send': Send,
  // Healthcare
  'hospital': Cross,
  'cross': Cross,
  'stethoscope': Stethoscope,
  'user-round': UserRound,
  'smile': Smile,
  'pill': Pill,
  'paw-print': PawPrint,
  'heart-pulse': HeartPulse,
  // Education
  'school': School,
  'baby': Baby,
  'book-open': BookOpen,
  'graduation-cap': GraduationCap,
  'library': Library,
  // Transport
  'bus': Bus,
  'car-taxi-front': Car,
  'parking-circle': ParkingSquare,
  'fuel': Fuel,
  'car': Car,
  'wrench': Wrench,
  'bike': Bike,
  'train': Train,
  'ship': Ship,
  'plane': Plane,
  'plane-takeoff': PlaneTakeoff,
  'helicopter': PlaneLanding,
  'route': Route,
  'navigation-2': Navigation2,
  'signpost': Signpost,
  'footprints': Footprints,
  'person-standing': PersonStanding,
  'arrow-up-down': ArrowUpDown,
  'traffic-cone': TrafficCone,
  'lamp': Lamp,
  // Accommodation
  'hotel': Hotel,
  'bed': Bed,
  'bed-double': BedDouble,
  'bed-single': BedSingle,
  'home': Home,
  'house': Home,
  // Shopping
  'shopping-bag': ShoppingBag,
  'shirt': Shirt,
  'smartphone': Smartphone,
  'sofa': Sofa,
  // Government
  'building-2': Building2,
  'scale': Scale,
  'shield': Shield,
  'flame': Flame,
  'mail': Mail,
  'briefcase': Briefcase,
  'users': Users,
  'scissors': Scissors,
  // Industry & land use
  'factory': Factory,
  'warehouse': Warehouse,
  'package': Package,
  'tractor': Tractor,
  'wheat': Wheat,
  'beef': Beef,
  'axe': Axe,
  'pickaxe': Axe,
  'trash-2': Trash2,
  'recycle': Recycle,
  'dribbble': Dribbble,
  'fence': Fence,
  'construction': Construction,
  'hard-hat': HardHat,
  // Religious
  'church': Church,
  // Leisure & Tourism
  'trees': Trees,
  'tree-pine': TreePine,
  'tree-deciduous': TreeDeciduous,
  'palmtree': Palmtree,
  'apple': Apple,
  'sprout': Sprout,
  'leaf': Leaf,
  'shrub': Shrub,
  'sun': Sun,
  'cloud-rain': CloudRain,
  'mountain': Mountain,
  'triangle': Triangle,
  'circle': Circle,
  'circle-dot': CircleDot,
  'flower': Flower2,
  'flower-2': Flower2,
  'droplets': Droplets,
  'droplet': Droplet,
  'land-plot': LandPlot,
  'mountain-snow': MountainSnow,
  'layers': Layers,
  'ferris-wheel': FerrisWheel,
  'dumbbell': Dumbbell,
  'waves': Waves,
  'trophy': Trophy,
  'umbrella': Umbrella,
  'star': Star,
  'eye': Eye,
  'palette': Palette,
  // Entertainment & Craft
  'clapperboard': Clapperboard,
  'drama': Drama,
  'camera': Camera,
  // Structures & barriers
  'brick-wall': BrickWall,
  'door-open': DoorOpen,
  'castle': Castle,
  'crown': Crown,
  'cylinder': Cylinder,
  'minus': Minus,
  'move-horizontal': MoveHorizontal,
  'square': Square,
  'log-in': LogIn,
  'lock': Lock,
  'blocks': Blocks,
  'ruin': Castle,
  'milestone': Milestone,
  // Water & waterways
  'anchor': Anchor,
  'database': Database,
  'wind': Wind,
  'pipette': Pipette,
  'rotate-ccw': RotateCcw,
  // Places & navigation
  'map': Map,
  'grid-2x2': Grid2x2,
  'trending-up': TrendingUp,
  // Generic
  'map-pin': MapPin,
  'navigation': Navigation,
  'building': Building,
  'file-text': FileText,
};

// Tailwind color classes mapping
export const colorClasses: Record<string, { bg: string; text: string; border: string }> = {
  orange: { bg: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-200' },
  amber: { bg: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-200' },
  green: { bg: 'bg-green-500', text: 'text-green-600', border: 'border-green-200' },
  emerald: { bg: 'bg-emerald-500', text: 'text-emerald-600', border: 'border-emerald-200' },
  blue: { bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-200' },
  red: { bg: 'bg-red-500', text: 'text-red-600', border: 'border-red-200' },
  cyan: { bg: 'bg-cyan-500', text: 'text-cyan-600', border: 'border-cyan-200' },
  teal: { bg: 'bg-teal-500', text: 'text-teal-600', border: 'border-teal-200' },
  indigo: { bg: 'bg-indigo-500', text: 'text-indigo-600', border: 'border-indigo-200' },
  purple: { bg: 'bg-purple-500', text: 'text-purple-600', border: 'border-purple-200' },
  pink: { bg: 'bg-pink-500', text: 'text-pink-600', border: 'border-pink-200' },
  rose: { bg: 'bg-rose-500', text: 'text-rose-600', border: 'border-rose-200' },
  violet: { bg: 'bg-violet-500', text: 'text-violet-600', border: 'border-violet-200' },
  sky: { bg: 'bg-sky-500', text: 'text-sky-600', border: 'border-sky-200' },
  yellow: { bg: 'bg-yellow-500', text: 'text-yellow-600', border: 'border-yellow-200' },
  slate: { bg: 'bg-slate-500', text: 'text-slate-600', border: 'border-slate-200' },
  stone: { bg: 'bg-stone-500', text: 'text-stone-600', border: 'border-stone-200' },
  lime: { bg: 'bg-lime-500', text: 'text-lime-600', border: 'border-lime-200' },
  gray: { bg: 'bg-gray-500', text: 'text-gray-600', border: 'border-gray-200' },
};

/**
 * Selected-state tiles for Appearance (map style / app theme), aligned with {@link colorClasses} hues.
 */
export const appearanceTileAccent: Record<
  'emerald' | 'indigo' | 'cyan' | 'green' | 'blue' | 'amber' | 'slate' | 'teal',
  { card: string; iconWell: string; icon: string; label: string }
> = {
  emerald: {
    card: 'border-2 border-emerald-500 dark:border-emerald-400 bg-emerald-50/90 dark:bg-emerald-950/40 shadow-sm',
    iconWell: 'bg-emerald-100 dark:bg-emerald-900/50',
    icon: 'text-emerald-600 dark:text-emerald-400',
    label: 'text-emerald-700 dark:text-emerald-300',
  },
  indigo: {
    card: 'border-2 border-indigo-500 dark:border-indigo-400 bg-indigo-50/90 dark:bg-indigo-950/40 shadow-sm',
    iconWell: 'bg-indigo-100 dark:bg-indigo-900/50',
    icon: 'text-indigo-600 dark:text-indigo-400',
    label: 'text-indigo-700 dark:text-indigo-300',
  },
  cyan: {
    card: 'border-2 border-cyan-500 dark:border-cyan-400 bg-cyan-50/90 dark:bg-cyan-950/40 shadow-sm',
    iconWell: 'bg-cyan-100 dark:bg-cyan-900/50',
    icon: 'text-cyan-600 dark:text-cyan-400',
    label: 'text-cyan-700 dark:text-cyan-300',
  },
  green: {
    card: 'border-2 border-green-500 dark:border-green-400 bg-green-50/90 dark:bg-green-950/40 shadow-sm',
    iconWell: 'bg-green-100 dark:bg-green-900/50',
    icon: 'text-green-600 dark:text-green-400',
    label: 'text-green-700 dark:text-green-300',
  },
  blue: {
    card: 'border-2 border-blue-500 dark:border-blue-400 bg-blue-50/90 dark:bg-blue-950/40 shadow-sm',
    iconWell: 'bg-blue-100 dark:bg-blue-900/50',
    icon: 'text-blue-600 dark:text-blue-400',
    label: 'text-blue-700 dark:text-blue-300',
  },
  amber: {
    card: 'border-2 border-amber-500 dark:border-amber-400 bg-amber-50/90 dark:bg-amber-950/40 shadow-sm',
    iconWell: 'bg-amber-100 dark:bg-amber-900/50',
    icon: 'text-amber-600 dark:text-amber-400',
    label: 'text-amber-700 dark:text-amber-300',
  },
  slate: {
    card: 'border-2 border-slate-500 dark:border-slate-400 bg-slate-50/90 dark:bg-slate-950/40 shadow-sm',
    iconWell: 'bg-slate-100 dark:bg-slate-800/80',
    icon: 'text-slate-600 dark:text-slate-300',
    label: 'text-slate-700 dark:text-slate-200',
  },
  teal: {
    card: 'border-2 border-teal-500 dark:border-teal-400 bg-teal-50/90 dark:bg-teal-950/40 shadow-sm',
    iconWell: 'bg-teal-100 dark:bg-teal-900/50',
    icon: 'text-teal-600 dark:text-teal-400',
    label: 'text-teal-700 dark:text-teal-300',
  },
};

interface DynamicIconProps extends Omit<LucideProps, 'ref'> {
  name: string;
}

/**
 * Renders a Lucide icon dynamically by name
 */
export function DynamicIcon({ name, ...props }: DynamicIconProps) {
  const IconComponent = iconComponents[name] || MapPin;
  return <IconComponent {...props} />;
}

interface CategoryIconProps {
  icon: string;
  color: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'filled' | 'outline' | 'ghost';
}

/**
 * Renders a category icon with background color
 */
export function CategoryIcon({ icon, color, size = 'md', variant = 'filled' }: CategoryIconProps) {
  const colors = colorClasses[color] || colorClasses.gray;

  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  const iconSizes = {
    sm: 12,
    md: 16,
    lg: 20,
  };

  if (variant === 'filled') {
    return (
      <div className={`${sizeClasses[size]} ${colors.bg} rounded-lg flex items-center justify-center`}>
        <DynamicIcon name={icon} size={iconSizes[size]} className="text-white" />
      </div>
    );
  }

  if (variant === 'outline') {
    return (
      <div className={`${sizeClasses[size]} border-2 ${colors.border} rounded-lg flex items-center justify-center`}>
        <DynamicIcon name={icon} size={iconSizes[size]} className={colors.text} />
      </div>
    );
  }

  // ghost variant
  return (
    <div className={`${sizeClasses[size]} flex items-center justify-center`}>
      <DynamicIcon name={icon} size={iconSizes[size]} className={colors.text} />
    </div>
  );
}

export default DynamicIcon;
